import { apiClient } from './api';

const notificationApi = {
  // ── Notifications list & counts ────────────────────────────────────────────
  getNotifications: (page = 1, filter = 'all') =>
    apiClient.get('/notifications', { params: { page, filter } }).then((r) => r.data),

  getUnreadCount: () =>
    apiClient.get('/notifications/unread-count').then((r) => r.data.unread_count),

  markAsRead: (id) =>
    apiClient.put(`/notifications/${id}/mark-as-read`).then((r) => r.data),

  markAllRead: () =>
    apiClient.put('/notifications/mark-all-read').then((r) => r.data),

  deleteNotification: (id) =>
    apiClient.delete(`/notifications/${id}`).then((r) => r.data),

  // ── Push subscriptions ─────────────────────────────────────────────────────
  getVapidPublicKey: () =>
    apiClient.get('/vapid-public-key').then((r) => r.data.public_key),

  registerSubscription: (subscription, platform = 'web') =>
    apiClient.post('/push-subscription/register', { subscription, platform }).then((r) => r.data),

  unregisterSubscription: (endpoint = null) =>
    apiClient.delete('/push-subscription/unregister', { data: endpoint ? { endpoint } : {} }).then((r) => r.data),

  listSubscriptions: () =>
    apiClient.get('/push-subscription/list').then((r) => r.data),

  // ── Preferences ────────────────────────────────────────────────────────────
  getPreferences: () =>
    apiClient.get('/notification-preferences').then((r) => r.data),

  setPreferences: (prefs) =>
    apiClient.put('/notification-preferences', prefs).then((r) => r.data),

  // ── Test ───────────────────────────────────────────────────────────────────
  sendTestNotification: () =>
    apiClient.post('/test-notification').then((r) => r.data),
};

// ── Push subscription helpers ──────────────────────────────────────────────────

/** Convert a base64url VAPID key to a Uint8Array for pushManager.subscribe(). */
export function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

/**
 * Subscribe the current browser to push notifications.
 * Returns the PushSubscription object or null on failure.
 */
export async function subscribeToPush(vapidPublicKey) {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return null;
  try {
    // Use a timeout so we don't hang if the SW isn't active yet
    const readyWithTimeout = Promise.race([
      navigator.serviceWorker.ready,
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Service worker not ready after 8 s')), 8000)
      ),
    ]);
    const reg = await readyWithTimeout;
    const existing = await reg.pushManager.getSubscription();
    if (existing) return existing;
    return await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    });
  } catch {
    return null;
  }
}

/** Unsubscribe the current browser from push notifications. */
export async function unsubscribeFromPush() {
  if (!('serviceWorker' in navigator)) return false;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) await sub.unsubscribe();
    return true;
  } catch {
    return false;
  }
}

export default notificationApi;
